import React, {useEffect, useState} from 'react';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon, IonLoading, IonModal,
  IonPage,
  IonRippleEffect,
  IonRow,
  IonText, IonToast,
  isPlatform
} from '@ionic/react';
import {
  logoFacebook,
  logoInstagram,
  logoTwitter,
} from 'ionicons/icons';

// Swiper React Bileşenleri ve Stilleri
import {Swiper, SwiperSlide} from 'swiper/react';
import {Pagination} from 'swiper';

import 'swiper/css';
import 'swiper/css/pagination';
import './MobileViewContainer.css';
import {InAppBrowser, ToolbarPosition} from "@capacitor/inappbrowser";
import {AuthContext} from "../providers/authContext";
import {MenuItem} from "../ebelediye/entities/menu/MenuItem";
import Icon from "./Icon";
import {useBaylanCard} from "../hooks/useBaylanCard";
import {useHistory} from "react-router-dom";
import WeatherBadge from "./WeatherBadge";
import {createTapHandlers} from "../utils/TapHandlers";

const MobileViewContainer: React.FC = () => {
  const { logo, kurumBilgileri } = React.useContext(AuthContext);
  const menus: Array<MenuItem> | undefined = kurumBilgileri?.mobileAppMenus;
  const { hasNfc, readCard } = useBaylanCard();
  const history = useHistory();
  const [toastMessage, setToastMessage] = useState<string>();
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [nfcAvailable, setNfcAvailable] = React.useState(true);
  const [isNfcModalOpen, setIsNfcModalOpen] = React.useState(false);

  const filteredMenus = (menus || []).filter(item => {
    if (item.title === 'NFC') {
      return isPlatform("android") && nfcAvailable;
    }
    return true;
  });

  useEffect(() => {
    const isAndroidPlatform = isPlatform("android");
    console.log('isAndroidPlatform: ', isAndroidPlatform);
    if (!isAndroidPlatform) return;

    let cancelled = false;

    (async () => {
      try {
        console.log('Checking for NFC support...');
        // Requires an NFC plugin (example shown for @capawesome/capacitor-nfc)
        const availability = await hasNfc();
        console.log('Nfc availability: ', availability);
        if (!cancelled) setNfcAvailable(availability);
      } catch {
        // Plugin missing / running on web / not supported
        if (!cancelled) setNfcAvailable(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasNfc]);

  const chunkArray = <T,>(arr: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  // Bir slide'da kaç kart olursa olsun, satır/kolon sayısını sabit tutar
  // (6 kart = 3 satır x 2 kolon). Son slide'da örneğin tek kart kalsa bile
  // grid tam genişlik/yükseklikte render edilir; böylece Swiper'ın dokunma
  // alanı daralmaz ve boş görünen kısımlar da kaydırmaya tepki verir.
  const ITEMS_PER_SLIDE = 6;

  const padToFullSlide = (items: Array<MenuItem>): Array<MenuItem | null> => {
    const padded: Array<MenuItem | null> = [...items];
    while (padded.length < ITEMS_PER_SLIDE) {
      padded.push(null);
    }
    return padded;
  };

  // Not: touch/click tutarlılığı için kullanılan createTapHandlers artık
  // ../utils/tapHandlers dosyasından import ediliyor (WeatherBadge ile ortak).

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--mobile-header-bg',
      "url('/assets/mobile-header-bg.jpg')"
    );
  }, []);

  const openSite = async (label: string, url: string) => {
    await InAppBrowser.openInWebView({
      url,
      options: {
        showToolbar: true,
        toolbarPosition: ToolbarPosition.TOP,
        closeButtonText: `← ${label}`, // "← Başkan" gibi görünür
        showURL: false,
        showNavigationButtons: false,
      } as any,
    });

    await InAppBrowser.addListener('browserClosed', () => {
      // gerekirse state temizle
    });
  };

  const openExternalSite = async (url: string) => {
    await InAppBrowser.openInExternalBrowser({ url } as any);
  };

  return (
    <IonPage>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        message={"Lütfen Bekleyin..."}
      />
      <IonContent fullscreen className="app-background">
        <IonToast
          isOpen={toastMessage !== undefined}
          onDidDismiss={() => setToastMessage(undefined)}
          message={toastMessage}
          duration={2000}
          position="top"
        />
        {/* Kavisli Üst Bilgi Alanı */}
        <div className="curved-header">
          <WeatherBadge />
          <div className="header-content">
            <div className="logo-container">
              {/*<img src="/assets/logo.png" alt="Belediye Logosu" className="logo-image" />*/}
              <img src={logo} alt="Belediye Logosu" className="logo-image" />
            </div>
            <IonText color="light">
              <h2 className="title-main">{kurumBilgileri?.kisaTanim}</h2>
              <p className="title-sub">e-Belediye Hizmetleri</p>
              <p className="title-motto">Hayatınızı Kolaylaştırın</p>
            </IonText>
          </div>
        </div>

        {/* Swiper ile Kaydırılabilir Menü Alanı */}
        <div className="swiper-menu-container">
          <Swiper
            modules={[Pagination]}
            pagination={{ clickable: true }}
            spaceBetween={20}
            slidesPerView={1}
            threshold={10}
            preventClicks={false}
            preventClicksPropagation={false}
          >
            {menus && chunkArray(filteredMenus, 6).map((slideItems, slideIndex) => (
              <SwiperSlide key={slideIndex}>
                <IonGrid className="main-menu-grid">
                  {chunkArray(padToFullSlide(slideItems), 2).map((rowItems, rowIndex) => (
                    <IonRow key={rowIndex}>
                      {rowItems.map((item, itemIndex) => (
                        item === null ? (
                          // Kart olmayan hücreler için görünmez placeholder:
                          // grid'in genişlik/yüksekliği hep sabit kalır, bu
                          // yüzden Swiper'ın dokunma alanı da daralmaz.
                          <IonCol size="6" key={itemIndex} aria-hidden="true" />
                        ) : (
                          <IonCol size="6" key={itemIndex}>
                            {item.title === 'NFC' ? (
                              isPlatform("android") && nfcAvailable && (
                                <IonCard className="menu-card ion-activatable" {...createTapHandlers(async () => {
                                  setIsNfcModalOpen(true);
                                  try {
                                    console.log('NFC Card Reading...');
                                    const result = await readCard(); // kart değene kadar burada bekler
                                    console.log('NFC Card Read Result: ', result);
                                    if (result.kod === '00') {
                                      if (Number(result.aboneNo) === 0) {
                                        setIsNfcModalOpen(false);
                                        alert("Karta tanımlı bir abone bilgisi bulunamadı. Lütfen başka bir kart ile deneyin.");
                                        return;
                                      }
                                      setIsNfcModalOpen(false);

                                      history.push("/page/NfcCardOperations", {
                                        nfcCardInfo: result,
                                      });
                                    } else {
                                      setToastMessage(`Okuma başarısız: ${result.aciklama}`);
                                    }
                                  } catch (err: any) {
                                    console.error("NFC Error: ", err);
                                    setToastMessage(err.message);
                                  }
                                })}>
                                  <IonCardContent className="ion-text-center">
                                    <img src="/assets/musluk.png" alt="musluk.png" className="menu-icon nfc-icon"/>
                                    <IonText><h3>SU YÜKLEME</h3></IonText>
                                  </IonCardContent>
                                  <IonRippleEffect />
                                </IonCard>
                              )
                            ) : (
                              <IonCard
                                className="menu-card ion-activatable"
                                {...createTapHandlers(
                                  item.url && (item.url.startsWith('http://') || item.url.startsWith('https://'))
                                    ? () => item.external ? openExternalSite(item.url) : openSite(item.title, item.url)
                                    : item.content || item.url === '/page/ExploreContainer'
                                      ? () =>
                                        history.push('/page/ExploreContainer', {
                                          title: item.title,
                                          content: item.content,
                                        })
                                      : () => history.push(item.url)
                                )}
                              >
                                <IonCardContent className="ion-text-center">
                                  {item.iconUrl ? (
                                    <img src={item.iconUrl} className="menu-icon" alt={item.title} style={{ width: '40px', height: '40px', marginBottom: '8px' }} />
                                  ) : (
                                    <Icon ios={item.iosIcon} md={item.mdIcon} className="menu-icon" slot="" />
                                  )}
                                  <IonText><h3>{item.title.toUpperCase()}</h3></IonText>
                                </IonCardContent>
                                <IonRippleEffect />
                              </IonCard>
                            )}
                          </IonCol>
                        )
                      ))}
                    </IonRow>
                  ))}
                </IonGrid>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Sosyal Medya Butonları */}
          <div className="social-media-container">
            <a href="https://www.facebook.com/ortakoybelediyesi68" target="_blank" rel="noopener noreferrer" className="social-button">
              <IonIcon icon={logoFacebook} />
            </a>
            <a href="https://www.instagram.com/ortakoybelediyesi" target="_blank" rel="noopener noreferrer" className="social-button">
              <IonIcon icon={logoInstagram} />
            </a>
            <a href="https://x.com/ortakoybelediye" target="_blank" rel="noopener noreferrer" className="social-button">
              <IonIcon icon={logoTwitter} />
            </a>
          </div>
        </div>

        <IonModal
          isOpen={isNfcModalOpen}
          onDidDismiss={() => setIsNfcModalOpen(false)}
          initialBreakpoint={0.5}
          breakpoints={[0, 0.5, 0.75]}
        >
          <IonContent className="ion-padding">
            <IonText>
              <h2>Kartı Okutunuz</h2>
              <p>Lütfen telefonunuzu Baylan NFC kartının yakınında sabit tutun.</p>
            </IonText>

            <div style={{ display: "flex", justifyContent: "center", margin: "16px 0" }}>
              <img
                src="assets/NFC.gif"
                alt="NFC"
                style={{ width: 225, maxWidth: "80%", height: "auto" }}
              />
            </div>

            <IonButton expand="block" onClick={() => setIsNfcModalOpen(false)}>
              Vazgeç
            </IonButton>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default MobileViewContainer;
